import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiMapPin, FiHeart, FiChevronRight } from 'react-icons/fi';
import { shedService } from '../services/shedService';
import { useWishlist } from '../context/WishlistContext';
import { cityDataMap, getNearbyCities, citiesByState, stateNames } from '../data/cityData';

const CityPage = () => {
  const { citySlug } = useParams();
  const [sheds, setSheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toggleWishlist, isWishlisted } = useWishlist();

  const city = cityDataMap[citySlug] || null;

  useEffect(() => {
    loadSheds();
    window.scrollTo(0, 0);
  }, [citySlug]);

  const loadSheds = async () => {
    setLoading(true);
    try {
      const { data } = await shedService.getAvailableSheds();
      if (data) {
        if (city) {
          const nearby = data.filter(s =>
            s.dealerLocation?.toLowerCase().includes(city.state.toLowerCase())
          );
          setSheds(nearby.length > 0 ? nearby : data.slice(0, 6));
        } else {
          setSheds(data.slice(0, 6));
        }
      }
    } catch (err) {
      console.error('Error loading city sheds:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const getRtoMonthly = (shed) => {
    const base = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
    return formatPrice(Math.round((base * 1.45) / 36));
  };

  const avgPrice = useMemo(() => {
    if (sheds.length === 0) return null;
    const total = sheds.reduce((sum, s) => sum + (s.salePrice || s.price), 0);
    return formatPrice(Math.round(total / sheds.length));
  }, [sheds]);

  const nearbyCities = useMemo(() => getNearbyCities(citySlug, 12), [citySlug]);
  const sameStateCities = useMemo(() => {
    if (!city) return [];
    return (citiesByState[city.state] || []).filter(c => c.slug !== citySlug);
  }, [citySlug, city]);

  const cityName = city?.full || citySlug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Your Area';
  const shortName = city?.name || cityName;
  const stateName = city?.stateName || '';

  // SEO: unique title, H1, meta desc, OG desc per four-way diversity rule
  const seoTitle = `Sheds for Sale in ${shortName} ${city?.state || ''} — Storage Buildings & Barns`;
  const seoH1 = `Find the Right Shed in ${cityName}`;
  const seoDescription = `Compare ${sheds.length || 'available'} sheds for sale near ${cityName}. Barn, gable, lean-to and workshop styles from local dealers. Rent-to-own with no credit check. Free delivery on select models.`;
  const seoOgDescription = `${shortName} homeowners are saving thousands on portable buildings. Browse shed styles, sizes and prices from verified ${stateName || 'local'} dealers.`;
  const canonicalUrl = `https://shedx.com/sheds-for-sale/${citySlug}`;

  // FAQ data for FAQ schema
  const faqs = [
    {
      q: `How much do sheds cost in ${shortName}?`,
      a: `Shed prices in ${cityName} range from about $1,500 for a small 6x8 utility shed to $20,000+ for a large 14x40 workshop with electrical. Most popular sizes (10x12 to 12x16) fall between $3,000 and $8,000.`
    },
    {
      q: `Do I need a permit for a shed in ${shortName}?`,
      a: `Permit requirements vary by jurisdiction. In ${shortName}, sheds under 120 square feet typically do not require a building permit. Larger structures may need a permit from your local building department. Check with ${city?.name || 'your city'} zoning before ordering.`
    },
    {
      q: `Can I rent-to-own a shed in ${shortName}?`,
      a: `Yes. Most dealers serving ${cityName} offer rent-to-own financing with no credit check. Terms typically run 36 to 60 months. You can pay off early without penalty.`
    },
    {
      q: `Is delivery included when buying a shed in ${shortName}?`,
      a: `Many dealers include free delivery within 50 miles. Beyond that, delivery fees usually run $3-$5 per mile. Professional setup is often included at no extra cost.`
    },
    {
      q: `What shed styles are popular in ${stateName || 'this area'}?`,
      a: `The most popular styles in ${stateName || 'this region'} are barn-style sheds (for loft storage), gable sheds (classic look) and lean-to sheds (for narrow lots). Workshops with electrical and insulation are growing in demand.`
    }
  ];

  // JSON-LD schema
  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: seoTitle,
        description: seoDescription,
        url: canonicalUrl,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shedx.com/' },
            { '@type': 'ListItem', position: 2, name: 'Sheds', item: 'https://shedx.com/sheds' },
            { '@type': 'ListItem', position: 3, name: `${shortName} ${city?.state || ''}`, item: canonicalUrl }
          ]
        }
      },
      {
        '@type': 'ItemList',
        name: `Sheds for Sale in ${cityName}`,
        numberOfItems: sheds.length,
        itemListElement: sheds.slice(0, 8).map((shed, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Product',
            name: shed.name,
            description: shed.description || `${shed.size} ${shed.shedType} shed`,
            image: shed.images?.[0],
            url: `https://shedx.com/shed/${shed.id}`,
            offers: {
              '@type': 'Offer',
              price: shed.salePrice || shed.price,
              priceCurrency: 'USD',
              availability: shed.status === 'available' ? 'https://schema.org/InStock' : 'https://schema.org/LimitedAvailability'
            }
          }
        }))
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a }
        }))
      },
      {
        '@type': 'LocalBusiness',
        name: 'ShedX',
        description: `Shed marketplace serving ${cityName}`,
        url: 'https://shedx.com',
        areaServed: { '@type': 'City', name: shortName, containedInPlace: { '@type': 'State', name: stateName } }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoOgDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        {/* Hero */}
        <div className="bg-white border-b border-gray-200 py-10">
          <div className="container mx-auto px-4 max-w-5xl">
            <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-gray-600">Home</Link>
              <FiChevronRight className="w-3 h-3" />
              <Link to="/sheds" className="hover:text-gray-600">Sheds</Link>
              {stateName && (
                <>
                  <FiChevronRight className="w-3 h-3" />
                  <Link to={`/sheds-for-sale/state/${city?.state?.toLowerCase()}`} className="hover:text-gray-600">{stateName}</Link>
                </>
              )}
              <FiChevronRight className="w-3 h-3" />
              <span className="text-gray-700">{shortName}</span>
            </nav>
            <h1 className="text-3xl font-semibold text-gray-900 mb-3">{seoH1}</h1>
            <p className="text-gray-500 max-w-2xl mb-6">
              Browse portable buildings, storage sheds, barns and workshops from verified dealers near {cityName}. Every listing includes full specs, color options and financing details.
            </p>

            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">Available</span>
                <span className="font-medium text-gray-900">{sheds.length} sheds</span>
              </div>
              {avgPrice && (
                <div>
                  <span className="text-gray-400 block text-xs uppercase tracking-wider">Avg. Price</span>
                  <span className="font-medium text-gray-900">{avgPrice}</span>
                </div>
              )}
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">Financing</span>
                <span className="font-medium text-gray-900">RTO available</span>
              </div>
              <div>
                <span className="text-gray-400 block text-xs uppercase tracking-wider">Delivery</span>
                <span className="font-medium text-gray-900">Free on select models</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl py-8">
          {/* Listings */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white h-80 animate-pulse" />
              ))}
            </div>
          ) : sheds.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 mb-4">No sheds listed near {cityName} yet.</p>
              <Link to="/shed-wanted" className="inline-block bg-gray-900 text-white text-sm font-medium px-6 py-3 hover:bg-gray-800 transition-colors">
                Tell us what you need
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
              {sheds.map((shed) => (
                <article key={shed.id} className="bg-white relative group">
                  <div className="absolute top-3 right-3 z-10 flex gap-1">
                    <button onClick={() => toggleWishlist(shed.id)} className="p-1.5 bg-white/80 hover:bg-white transition-colors">
                      <FiHeart className={`w-4 h-4 ${isWishlisted(shed.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                    </button>
                  </div>
                  <Link to={`/shed/${shed.id}`} className="block">
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                      <img src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'} alt={`${shed.name} for sale in ${shortName}`} loading="lazy" className="w-full h-full object-cover" />
                      {shed.salePrice && shed.salePrice < shed.price && (
                        <span className="absolute top-3 left-3 bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">Sale</span>
                      )}
                      {shed.images?.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-0.5">{shed.images.length} photos</span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">{shed.shedType} &middot; {shed.size}</p>
                        <span className={`flex items-center gap-1 text-[11px] font-medium ${shed.status === 'available' ? 'text-green-600' : 'text-amber-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${shed.status === 'available' ? 'bg-green-500' : 'bg-amber-500'}`} />
                          {shed.status === 'available' ? 'Available' : 'Reserved'}
                        </span>
                      </div>
                      <h3 className="font-medium text-gray-900 mb-1 group-hover:underline">{shed.name}</h3>
                      {shed.dealerName && (
                        <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                          <FiMapPin className="w-3 h-3" />
                          {shed.dealerName}{shed.dealerLocation ? ` — ${shed.dealerLocation}` : ''}
                        </p>
                      )}
                      <div className="flex items-baseline gap-2 mb-1">
                        {shed.salePrice && shed.salePrice < shed.price ? (
                          <>
                            <span className="text-sm text-gray-400 line-through">{formatPrice(shed.price)}</span>
                            <span className="text-lg font-semibold text-red-600">{formatPrice(shed.salePrice)}</span>
                          </>
                        ) : (
                          <span className="text-lg font-semibold text-gray-900">{formatPrice(shed.price)}</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400">or from <span className="text-gray-600 font-medium">{getRtoMonthly(shed)}/mo</span> with Rent-to-Own</p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-white border border-gray-200 p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Need a Custom Shed in {shortName}?</h2>
            <p className="text-sm text-gray-500 mb-5">Tell us the size, style and features you want. We'll match you with {stateName} dealers who can build it.</p>
            <Link to="/shed-wanted" className="inline-block bg-gray-900 text-white text-sm font-medium px-6 py-3 hover:bg-gray-800 transition-colors">
              Post a Shed Wanted Request
            </Link>
          </div>

          {/* SEO Content — Rich, unique paragraphs per city */}
          <div className="mt-12 space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Buying a Shed in {cityName}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {cityName} homeowners have a strong selection of portable buildings from local and regional dealers. Whether you need backyard storage, a dedicated workshop, a home office or a modern she-shed, manufacturers in {stateName || 'your area'} build everything from compact 6x8 utility sheds to full-size 16x40 garages with roll-up doors. Most {shortName} dealers deliver and set up the building on your prepared site at no extra cost within 50 miles.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Average prices in the {shortName} area start around $1,500 for a small lean-to and climb past $20,000 for insulated workshops with electrical, plumbing and interior finishes. Rent-to-own programs make it possible to spread the cost across 36 to 60 months with no credit check. You can also pay in full or put down a deposit to lock in today's price.
            </p>

            <h2 className="text-lg font-semibold text-gray-900 pt-4">Popular Shed Types in {shortName}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { type: 'Barn Sheds', desc: `Gambrel roof design maximizes overhead loft storage. Popular in ${shortName} for homeowners who need vertical space.` },
                { type: 'Gable Sheds', desc: 'Traditional A-frame roof with clean lines. Works well for backyard storage, garden tools and seasonal equipment.' },
                { type: 'Lean-To Sheds', desc: 'Single-slope roof ideal for narrow lots and side-yard placement. Budget-friendly starting under $2,000.' },
                { type: 'Workshops', desc: `Heavy-duty construction with workbench space, electrical outlets and overhead lighting. A top pick for ${shortName} DIYers.` }
              ].map(item => (
                <div key={item.type} className="bg-white border border-gray-200 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-1 text-sm">{item.type}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* FAQ Section */}
            <h2 className="text-lg font-semibold text-gray-900 pt-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-white border border-gray-200 rounded-lg group">
                  <summary className="px-5 py-4 text-sm font-medium text-gray-900 cursor-pointer list-none flex items-center justify-between">
                    {faq.q}
                    <FiChevronRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Same-state cities */}
          {sameStateCities.length > 0 && (
            <div className="mt-12">
              <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-3">More {stateName} cities</h3>
              <div className="flex flex-wrap gap-2">
                {sameStateCities.map(c => (
                  <Link
                    key={c.slug}
                    to={`/sheds-for-sale/${c.slug}`}
                    className="text-sm text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1.5 hover:border-gray-300 transition-colors"
                  >
                    {c.name}, {c.state}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Nearby cities (other states) */}
          {nearbyCities.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-3">Sheds in nearby cities</h3>
              <div className="flex flex-wrap gap-2">
                {nearbyCities.filter(c => c.state !== city?.state).slice(0, 8).map(c => (
                  <Link
                    key={c.slug}
                    to={`/sheds-for-sale/${c.slug}`}
                    className="text-sm text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1.5 hover:border-gray-300 transition-colors"
                  >
                    {c.full}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* State index link */}
          {stateName && (
            <div className="mt-8 text-center">
              <Link
                to={`/sheds-for-sale/state/${city?.state?.toLowerCase()}`}
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                View all sheds for sale in {stateName} &rarr;
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CityPage;
